/**
 * Der Abschnitt „DSV-Historie" (`ship-historie`) - ALLE rohen `shipment_events`-Zeilen der
 * verknuepften Sendung, UNGEFILTERT (`GET /api/orders/:orderId/shipment/ereignisse`,
 * `backend/src/http/routes/orderShipments.ts`). Anders als `Stationen.tsx` (der abgeleitete
 * Zeitstrahl, `istVerwertbaresEreignis()`-gefiltert) zeigt diese Liste auch ein Ereignis mit einem
 * Code ohne `STATION_ZU_STUFE`-Zuordnung UND ein storniertes Ereignis - letzteres bleibt sichtbar,
 * markiert mit der Klasse `ship-historie-storniert` (auf der `<tr>`) UND dem sichtbaren Text
 * „storniert" in der Status-Spalte (nicht nur Farbe - Barrierefreiheit, dasselbe Prinzip wie
 * `feldZustandClass` in `DsvSendung.tsx`).
 *
 * Tabellenform (Auftrag "DSV-Historie als Tabelle statt Kaesten-Liste", 2026-09-06): urspruenglich
 * eine Kasten-Liste (`<ul class="ship-historie-liste"><li>`), auf Nutzerwunsch durch eine echte
 * `<table class="ship-historie-tabelle">` mit sechs Spalten ersetzt (Eingang, Code, Ereignis, Ort,
 * Termin, Status) - die einzelnen `<td>` tragen dabei bewusst KEINE eigene Klasse mehr (anders als
 * vormals `.ship-historie-datum`/`-code`/`-beschreibung`/`-ort`/`-termin`/`-marke`): das Stylesheet
 * (`neubau/stationen.css`) greift stattdessen ueber die Spaltenposition (`nth-child`) zu, wie es
 * bei einer Tabelle mit fester Spaltenreihenfolge ueblich ist.
 *
 * Bauplan wie `Stationen.tsx`: eigene, aehnlich benannte CSS-Klassen (`ship-historie-*`) statt die
 * bestehenden `ship-station-*` fuer einen anderen Zweck umzuwidmen (Auftrag).
 *
 * `ort` (Auftrag "Ort in der DSV-Historie ergaenzen", 2026-09-05): `null` heisst kein Ort im
 * DSV-Event (leeres `location`-Objekt, z. B. beim Ereignis "Booking" gemessen) - in der Kasten-Liste
 * entfiel das Feld dafuer komplett, in der Tabelle bleibt die Zelle dagegen STEHEN (leer statt
 * weggelassen), damit die Spalten ueber alle Zeilen ausgerichtet bleiben. Weiterhin KEIN
 * Platzhaltertext wie "-" (anders als beim Termin, wo "—" einen erwarteten, aber fehlenden Wert
 * markiert - hier fehlt ein blosser Zusatz, das bleibt inhaltlich gleich, nur die Darstellung als
 * leere statt fehlende Zelle ist neu).
 *
 * "Eingang" und "Termin" (Auftrag "Zeitstempel der DSV-Historie in 'Eingang' und 'Termin'
 * auftrennen", 2026-09-05): die Spalte "Eingang" zeigt `eingang` (DSVs `eventLastModified`, mit
 * `received_at`-Rueckfall - IMMER befuellt, zugleich der Sortierschluessel des Servers), die Spalte
 * "Termin" zeigt `zeitpunkt` (DSVs `eventDate`) - bei einer Schaetzung (z. B. `ETA`) ist das bereits
 * der geschaetzte ZIELTERMIN selbst, NICHT der Zeitpunkt, an dem DSV diese Schaetzung gemeldet hat,
 * deshalb die getrennten Spalten. "Termin" zeigt "—" bei fehlendem Wert (anders als "Ort" entfaellt
 * "Termin" NIE - jedes Ereignis hat einen erwarteten, nur manchmal fehlenden Termin).
 */
import { fmtDT } from "../../../lib/format.ts";
import type { DsvEreignis } from "../../../lib/types.ts";

export interface DsvHistorieProps {
  /** `null`/`undefined` heisst: noch nicht geladen. */
  historie?: DsvEreignis[] | null;
}

export function DsvHistorie({ historie }: DsvHistorieProps) {
  if (!historie || historie.length === 0) {
    return <p className="calc-note">Keine DSV-Ereignisse vorhanden.</p>;
  }

  return (
    <table className="ship-historie-tabelle">
      <thead>
        <tr>
          <th>Eingang</th>
          <th>Code</th>
          <th>Ereignis</th>
          <th>Ort</th>
          <th>Termin</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {historie.map((ereignis, index) => (
          <tr key={index} className={ereignis.storniert ? "ship-historie-storniert" : undefined}>
            <td>{fmtDT(ereignis.eingang)}</td>
            <td>{ereignis.code}</td>
            <td>{ereignis.beschreibung}</td>
            <td>{ereignis.ort ?? ""}</td>
            <td>{ereignis.zeitpunkt ? fmtDT(ereignis.zeitpunkt) : "—"}</td>
            <td>{ereignis.storniert ? "storniert" : ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
